import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { directLogout } from "../../utils/tokenManager";
import FinishModal from "components/agent/commonSections.jsx/FinishModal";
import meeting from "../../assets/agent/meeting.svg";
import meetingRecoveryIcon from "../../assets/agent/recovery.svg";
import followUp from "../../assets/agent/followUp.svg";
import coldCalls from "../../assets/agent/coldCalls.svg";
import menu from "../../assets/dashboard/menu.svg";
import { useContactContext } from "../../contexts/ContactContext";

const Sidebar = ({ logoIcon, logout, labelClassName, finish, onToggle = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const role = sessionStorage.getItem("userRole");
  const [finishModal, setFinishModal] = useState(false);
  
  // Load pinned state from localStorage, default to false (unpinned)
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem("agentSidebarPinned");
    return saved === "true";
  });
  
  // Sidebar is expanded when pinned
  const isExpanded = isPinned;
  
  // Notify parent of initial state on mount
  useEffect(() => {
    onToggle(isPinned);
  }, []); // Only on mount

  const {
    meetingRecovery,
    meetingContacts,
    followUpContacts,
    coldCallContacts,
  } = useContactContext?.() ?? {
    meetingRecovery: [],
    meetingContacts: [],
    followUpContacts: [],
    coldCallContacts: [],
  };

  const handleLogo = () => {
    if (role === "Admin") {
      navigate("/dashboard");
    } else if (role === "Agent") {
      navigate("/agent");
    }
  };

  const handleFinish = async () => {
    setFinishModal(true);
  };
  const handleLogout = async () => {
    try {
      await directLogout();
      sessionStorage.clear();
      localStorage.clear();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const toggleSidebar = () => {
    const newPinnedState = !isPinned;
    setIsPinned(newPinnedState);
    // Persist to localStorage
    localStorage.setItem("agentSidebarPinned", String(newPinnedState));
    // Notify parent component about the change
    onToggle(newPinnedState);
  };

  const activeKey = useMemo(() => {
    const last = location.pathname.split("/").filter(Boolean).pop() || "";
    if (["agent", "agent-dashboard"].includes(last)) return "";
    return last;
  }, [location.pathname]);

  const navigationItems = [
    {
      key: "cold-calls",
      icon: coldCalls,
      alt: "Cold Calls",
      count: coldCallContacts?.length || 0,
      label: t("agent.cards.coldCalls"),
      onClick: () => navigate("/agent/workspace/cold-calls"),
    },
    {
      key: "follow-up",
      icon: followUp,
      alt: "Follow-ups",
      count: followUpContacts?.length || 0,
      label: t("agent.cards.FollowUp"),
      onClick: () => navigate("/agent/workspace/follow-up"),
    },
    {
      key: "meeting",
      icon: meeting,
      alt: "Meeting",
      count: meetingContacts?.length || 0,
      label: t("agent.cards.Meeting"),
      onClick: () => navigate("/agent/workspace/meeting"),
    },
    {
      key: "meeting-recovery",
      icon: meetingRecoveryIcon,
      alt: "Meeting Recovery",
      count: meetingRecovery?.length || 0,
      label: t("agent.cards.MeetingRecovery"),
      onClick: () => navigate("/agent/workspace/meeting-recovery"),
    },
  ];

  return (
    <div
      className={`fixed top-0 left-0 h-screen flex flex-col justify-between items-center py-6 transition-all duration-300 ${
        isExpanded ? "w-[250px]" : "w-[80px]"
      } z-50 ${labelClassName}`}
    >
      <div
        className={`flex gap-3 px-4 mb-8 items-center w-full ${
          isExpanded ? "justify-between" : "flex-col space-y-9"
        }`}
      >
        <div className="flex w-full">
          <img
            src={logoIcon}
            alt="Logo"
            className="h-[55px] w-[50px] cursor-pointer"
            onClick={handleLogo}
          />
        </div>
        <button
          onClick={toggleSidebar}
          className="text-white hover:opacity-80 transition-opacity"
          data-tour-id="sidebar-toggle"
          title={t("button.toggle")}
        >
          <img src={menu} alt={isPinned ? "unpin" : "pin"} />
        </button>
      </div>

      <div className="flex flex-col gap-6 flex-1 w-full mt-5" data-tour-id="navItem">
        {navigationItems.map((item) => {
          const isActive = activeKey === item.key;
          return (
            <div
              key={item.key}
              className={`relative group flex items-center gap-3 cursor-pointer transition-colors px-7 py-2 ${
                isExpanded ? "justify-start" : "justify-center"
              } ${isActive ? "bg-icon" : ""}`}
              onClick={item.onClick}
              
            >
              <div className="flex items-center gap-3" data-tour-id={`nav-${item.key}`}>
                <div className="relative">
                  {/* Handover icon - dark blue on hover */}
                  <img
                    src={item.icon}
                    alt={item.alt}
                    className="w-6 h-6 flex-shrink-0 transition-all group-hover:[filter:brightness(0)_saturate(100%)_invert(12%)_sepia(22%)_saturate(2103%)_hue-rotate(191deg)_brightness(96%)_contrast(98%)]"
                  />

                  {/* Only show circle if count > 0 */}
                  {item.count > 0 && (
                    <span
                      className={`absolute top-[-15px] right-[-15px] bg-[rgb(255_153_0/var(--tw-bg-opacity,1))] rounded-full text-[#051D42] text-xs font-bold min-w-[24px] h-6 px-2 flex items-center justify-center`}
                    >
                      {item.count}
                    </span>
                  )}
                </div>

                {isExpanded && (
                  <div className="flex items-center justify-between w-full gap-2">
                    {/* Label text color changed to dark blue */}
                    <span className="text-white text-s group-hover:text-[#051D42] transition-all">
                      {item.label}
                    </span>
                  </div>
                )}
              </div>

              {!isExpanded && (
                <span className="absolute left-full px-2 py-1 bg-white text-[#051D42] text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity">
                  {item.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        className={`flex flex-col gap-4 px-4 mt-5 items-center w-full ${
          isExpanded ? "justify-start" : "justify-center"
        }`}
      >
        <div data-tour-id="finish-day" className={`relative group ${isExpanded ? "self-start" : ""}`}>
          <img
            src={finish}
            alt="finish"
            className="w-[36px] cursor-pointer flex items-center justify-center"
            onClick={handleFinish}
            style={{ filter: "brightness(0) invert(1)" }}
          />
          {!isExpanded && (
            <span className="absolute left-full top-0 ml-2 px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-opacity">
              {t("agent.nextCall.finishTooltip")}
            </span>
          )}
        </div>
        <div
          className={`flex items-center gap-3 w-full ${
            isExpanded ? "justify-start" : "justify-center"
          }`}
        >
          <img
            src={logout}
            alt="logout"
            className="w-[36px] cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleLogout}
          />
        </div>
      </div>
      <FinishModal open={finishModal} onClose={() => setFinishModal(false)} />
    </div>
  );
};

export default Sidebar;